import { map, includes } from 'ramda';

type FormFields =
  | 'email'
  | 'givenName'
  | 'familyName'
  | 'code'
  | 'merchant'
  | 'userType'
  | 'userRole'
  | 'password'
  | 'newPassword'
  | 'tempPassword'
  | 'confirmPassword';

type Identities = {
  dateCreated?: number;
  issuer?: string;
  primary?: boolean;
  providerName?: string;
  providerType?: string;
  userId?: string;
};

type Profile = {
  merchantId: string;
  merchantName: string;
  email: string;
  emailVerified?: boolean;
  familyName: string;
  givenName: string;
  sub: string;
  userType: string;
  userRole: string;
  identities: Identities[];
};

type Merchant = {
  merchantId: string;
  merchantName: string;
};

type CurrentUserAttributes = {
  'custom:merchantId'?: string;
  'custom:merchantName'?: string;
  'custom:userType'?: string;
  'custom:userRole'?: string;
  email: string;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  sub: string;
  identities?: string;
};

const initIdentities = {
  dateCreated: null,
  issuer: null,
  primary: null,
  providerName: null,
  providerType: null,
  userId: null,
};

const userTypes = {
  options: [
    {
      key: 'filters',
      text: 'None',
      value: null,
    },
    {
      key: 'enterprise',
      text: 'Enterprise (External User)',
      value: 'enterprise',
    },
    {
      key: 'tier3',
      text: 'Tier 3',
      value: 'tier3',
    },
  ],
  label: {
    children: 'User Types',
  },
  placeholder: 'User Type',
};

const userRoles = {
  options: [
    {
      key: 'readonly',
      text: 'Read Only',
      value: 'readOnly',
    },
  ],
  label: {
    children: 'User Role',
  },
  placeholder: 'User Role',
};

const checkUserType = (currentUser: CurrentUserAttributes) => {
  const merchantId = currentUser['custom:merchantId'] || null;
  const merchantName = currentUser['custom:merchantName'] || null;
  const userType = currentUser['custom:userType'] || null;
  const domain = currentUser.email.split('@');

  if (userType) return userType;

  // needed for backward compatibility
  if (
    !includes(domain[1], ['dosh.cash', 'dosh.us', 'dosh.com', 'cardlytics.com'])
  ) {
    if (merchantId && merchantName) {
      return 'enterprise';
    }
  } else {
    return 'dosh';
  }

  return null;
};

const merchantTransformer = map(({ merchantName, merchantId }: Merchant) => ({
  key: merchantId,
  value: JSON.stringify({
    merchantName,
    merchantId,
  }),
  text: merchantName,
}));

const sanitizeMessage = (
  error: Record<string, string>,
  replaceMsg: string,
): string => {
  if (
    error.code === 'UserNotFoundException' ||
    error.code === 'PasswordResetRequiredException' ||
    error.code === 'NotAuthorizedException'
  ) {
    return replaceMsg;
  }
  return error.message;
};

const passwordRegex = new RegExp(
  "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_?`~|'])(?=.{8,})",
);

const passwordDoesNotMeetRequirements =
  'Password must contain: upper and lower case letters, a number, a special character, and be a minimum of 8 characters in length!';

const passwordsDoNotMatch = "Passwords don't match";

const noBlankPasswords = 'Password may not be blank';

export {
  checkUserType,
  initIdentities,
  merchantTransformer,
  CurrentUserAttributes,
  FormFields,
  Merchant,
  Profile,
  userTypes,
  userRoles,
  sanitizeMessage,
  passwordRegex,
  passwordDoesNotMeetRequirements,
  passwordsDoNotMatch,
  noBlankPasswords,
};
